/**
 * Magic-byte rules: each MIME maps to one or more prefix signatures (OR).
 * TIFF is two 4-byte alternatives; PDF is multiple prefix variants; most types use one variant.
 */
const MIME_MAGIC_PREFIXES = {
  'image/jpeg': [[0xff, 0xd8, 0xff]],
  'image/png': [[0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]],
  'image/gif': [[0x47, 0x49, 0x46, 0x38]],
  'image/webp': [[0x52, 0x49, 0x46, 0x46]],
  'image/bmp': [[0x42, 0x4d]],
  'image/tiff': [
    [0x49, 0x49, 0x2a, 0x00],
    [0x4d, 0x4d, 0x00, 0x2a],
  ],
  'image/x-icon': [[0x00, 0x00, 0x01, 0x00]],
  'image/heic': [[0x00, 0x00, 0x00, 0x20, 0x66, 0x74, 0x79, 0x70]],
  'image/heif': [[0x00, 0x00, 0x00, 0x20, 0x66, 0x74, 0x79, 0x70]],
  'image/avif': [[0x00, 0x00, 0x00, 0x20, 0x66, 0x74, 0x79, 0x70]],
  'application/pdf': [
    [0x25, 0x50, 0x44, 0x46],
    [0x25, 0x50, 0x44, 0x46, 0x2d],
    [0x25, 0x50, 0x44, 0x46, 0x2b],
    [0xef, 0xbb, 0xbf, 0x25, 0x50, 0x44, 0x46],
    [0x3c, 0x21, 0x3d, 0x3d],
  ],
  'application/msword': [[0xd0, 0xcf, 0x11, 0xe0]],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    [0x50, 0x4b, 0x03, 0x04],
  ],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
    [0x50, 0x4b, 0x03, 0x04],
  ],
  'application/zip': [[0x50, 0x4b, 0x03, 0x04]],
  'application/x-rar-compressed': [[0x52, 0x61, 0x72, 0x21]],
} as const satisfies Record<string, readonly (readonly number[])[]>

type MagicMime = keyof typeof MIME_MAGIC_PREFIXES

const EXTENSION_TO_MIME = {
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.svg': 'image/svg+xml',
  '.bmp': 'image/bmp',
  '.tiff': 'image/tiff',
  '.tif': 'image/tiff',
  '.ico': 'image/x-icon',
  '.heic': 'image/heic',
  '.heif': 'image/heif',
  '.avif': 'image/avif',
  '.pdf': 'application/pdf',
  '.doc': 'application/msword',
  '.docx':
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xls': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.txt': 'text/plain',
  '.csv': 'text/csv',
  '.zip': 'application/zip',
  '.rar': 'application/x-rar-compressed',
} as const

const SKIP_MAGIC_CHECK = new Set<string>([
  'text/plain',
  'text/csv',
  'image/svg+xml',
])

function maxPrefixLength(variants: readonly (readonly number[])[]): number {
  let n = 0
  for (const v of variants) n = Math.max(n, v.length)
  return Math.max(1, n)
}

function bufferMatchesPrefix(
  buffer: Uint8Array,
  prefix: readonly number[],
): boolean {
  return prefix.every((byte, i) => buffer[i] === byte)
}

function bufferMatchesAnyVariant(
  buffer: Uint8Array,
  variants: readonly (readonly number[])[],
): boolean {
  return variants.some((sig) => bufferMatchesPrefix(buffer, sig))
}

async function readFilePrefix(
  file: File,
  byteLength: number,
): Promise<Uint8Array | null> {
  if (byteLength <= 0) return null
  try {
    const head = file.slice(0, byteLength)
    if (typeof head.arrayBuffer === 'function') {
      return new Uint8Array(await head.arrayBuffer())
    }
    return await readBlobAsUint8ArrayViaFileReader(head)
  } catch {
    return null
  }
}

/**
 * Validates if a file's content matches its extension
 * @param file The file to validate
 * @returns Promise that resolves to true if the file signature matches its extension, false otherwise
 */
export async function validateFileSignature(file: File): Promise<boolean> {
  const extension = '.' + file.name.split('.').pop()?.toLowerCase()
  if (!extension) {
    return false
  }
  if (!(extension in EXTENSION_TO_MIME)) {
    return true
  }

  const expectedMime =
    EXTENSION_TO_MIME[extension as keyof typeof EXTENSION_TO_MIME]

  if (SKIP_MAGIC_CHECK.has(expectedMime)) {
    return true
  }

  if (!(expectedMime in MIME_MAGIC_PREFIXES)) {
    return false
  }

  const variants = MIME_MAGIC_PREFIXES[expectedMime as MagicMime]
  const probeLen = maxPrefixLength(variants)
  const buffer = await readFilePrefix(file, probeLen)
  if (!buffer) return false

  return bufferMatchesAnyVariant(buffer, variants)
}

/** Legacy fallback when Blob#arrayBuffer is unavailable. */
function readBlobAsUint8ArrayViaFileReader(blob: Blob): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const Reader = globalThis.FileReader
    if (typeof Reader !== 'function') {
      reject(new Error('FileReader unavailable'))
      return
    }
    const reader = new Reader()
    if (typeof reader.readAsArrayBuffer !== 'function') {
      reject(new Error('readAsArrayBuffer unavailable'))
      return
    }
    reader.onload = (e) => {
      resolve(new Uint8Array(e.target?.result as ArrayBuffer))
    }
    reader.onerror = () =>
      reject(reader.error ?? new Error('FileReader failed'))
    reader.readAsArrayBuffer(blob)
  })
}

/**
 * Gets the MIME type from a file extension
 * @param extension The file extension (with dot, e.g. '.jpg')
 * @returns The MIME type or undefined if not found
 */
export function getMimeTypeFromExtension(
  extension: string,
): string | undefined {
  return EXTENSION_TO_MIME[
    extension.toLowerCase() as keyof typeof EXTENSION_TO_MIME
  ]
}
